/**
 * 🎯 SHARED: useProjectConfig Composable
 *
 * Zentrale Verwaltung der Projekt-Konfiguration
 * Bietet typsichere Helper für SEO, Meta-Tags, Structured Data, etc.
 */
import { computed, inject } from 'vue';
// Injection Key für die Config
export const PROJECT_CONFIG_KEY = Symbol('projectConfig');
/**
 * Composable für Zugriff auf Projekt-Konfiguration
 */
export function useProjectConfig() {
    const config = inject(PROJECT_CONFIG_KEY);
    if (!config) {
        throw new Error('Project config not provided! Make sure to provide it in main.ts with app.provide(PROJECT_CONFIG_KEY, config)');
    }
    // === BASIC INFO ===
    const projectId = computed(() => config.project.id);
    const projectName = computed(() => config.project.name);
    const restaurantName = computed(() => config.restaurant.name);
    const restaurantShortName = computed(() => config.restaurant.shortName);
    // === CONTACT ===
    const phone = computed(() => config.restaurant.contact.phone);
    const email = computed(() => config.restaurant.contact.email);
    const website = computed(() => config.restaurant.contact.website);
    // === ADDRESS ===
    const address = computed(() => config.restaurant.address);
    const fullAddress = computed(() => {
        const addr = config.restaurant.address;
        return `${addr.street} ${addr.number}, ${addr.postalCode} ${addr.city}, ${addr.country}`;
    });
    // === BRANDING ===
    const logo = computed(() => config.branding.logo.light);
    const colors = computed(() => config.branding.colors);
    const primaryColor = computed(() => config.branding.colors.primary);
    // === UI / INTERACTIONS ===
    const uiSettings = computed(() => config.ui);
    const productInteractionSettings = computed(() => uiSettings.value?.productInteractions);
    // === SEO META TAGS ===
    const getMetaTags = (pageTitle, pageDescription) => {
        const title = pageTitle
            ? (config.seo.titleTemplate?.replace('{title}', pageTitle) || `${pageTitle} | ${restaurantName.value}`)
            : config.seo.title;
        const description = pageDescription || config.seo.description;
        const keywords = config.seo.keywords.join(', ');
        return {
            title,
            description,
            keywords,
            ogTitle: title,
            ogDescription: description,
            ogImage: config.seo.ogImage,
            twitterCard: config.seo.twitterCard || 'summary',
        };
    };
    // === STRUCTURED DATA (Schema.org) ===
    const getStructuredData = () => {
        const addr = config.restaurant.address;
        const structuredConfig = config.seo.structuredData;
        const data = {
            '@context': 'https://schema.org',
            '@type': structuredConfig.type,
            name: config.restaurant.name,
            image: structuredConfig.image,
            address: {
                '@type': 'PostalAddress',
                streetAddress: `${addr.street} ${addr.number}`,
                addressLocality: addr.city,
                postalCode: addr.postalCode,
                addressCountry: addr.countryCode
            },
            url: website.value,
            telephone: phone.value,
            servesCuisine: structuredConfig.servesCuisine,
            priceRange: structuredConfig.priceRange
        };
        // Geo Coordinates (optional)
        if (addr.coordinates) {
            data.geo = {
                '@type': 'GeoCoordinates',
                latitude: addr.coordinates.lat,
                longitude: addr.coordinates.lng
            };
        }
        // Opening Hours
        if (config.restaurant.openingHours) {
            const openingHoursSpec = [];
            const dayMap = {
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
                sunday: 'Sunday'
            };
            Object.entries(config.restaurant.openingHours).forEach(([day, hours]) => {
                if (hours.isOpen) {
                    openingHoursSpec.push({
                        '@type': 'OpeningHoursSpecification',
                        dayOfWeek: [dayMap[day]],
                        opens: hours.open,
                        closes: hours.close
                    });
                }
            });
            if (openingHoursSpec.length > 0) {
                data.openingHoursSpecification = openingHoursSpec;
            }
        }
        return data;
    };
    // === SERVICES ===
    const isDeliveryEnabled = computed(() => config.services.delivery.enabled);
    const isPickupEnabled = computed(() => config.services.pickup.enabled);
    const deliveryInfo = computed(() => config.services.delivery);
    const pickupInfo = computed(() => config.services.pickup);
    // === PAYMENT ===
    const availablePaymentMethods = computed(() => {
        const methods = [];
        if (config.payment.cash)
            methods.push('cash');
        if (config.payment.card)
            methods.push('card');
        if (config.payment.paypal)
            methods.push('paypal');
        if (config.payment.applePay)
            methods.push('applePay');
        if (config.payment.googlePay)
            methods.push('googlePay');
        return methods;
    });
    // === PRICING ===
    const formatPrice = (amountCent) => {
        return new Intl.NumberFormat(config.pricing.locale, {
            style: 'currency',
            currency: config.pricing.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amountCent / 100);
    };
    // === SOCIAL MEDIA ===
    const socialLinks = computed(() => config.restaurant.social || {});
    // === LEGAL ===
    const legalInfo = computed(() => config.legal);
    const imprintUrl = computed(() => config.legal.pages.imprint);
    const privacyUrl = computed(() => config.legal.pages.privacy);
    const termsUrl = computed(() => config.legal.pages.terms);
    // === FEATURES ===
    const features = computed(() => config.features);
    const isFeatureEnabled = (feature) => {
        return config.features[feature];
    };
    // === API CONFIGURATION ===
    const apiConfig = computed(() => config.deployment.api);
    const apiBaseUrl = computed(() => config.deployment.api.baseUrl);
    const publicApiUrl = computed(() => config.deployment.api.publicUrl);
    // === ANALYTICS ===
    const analytics = computed(() => config.deployment.analytics || {});
    const googleAnalyticsConfig = computed(() => {
        const ga = config.deployment.analytics?.googleAnalytics;
        if (!ga) {
            return null;
        }
        if (typeof ga === 'string') {
            const trimmed = ga.trim();
            if (!trimmed) {
                return null;
            }
            return {
                enabled: true,
                measurementId: trimmed
            };
        }
        return ga;
    });
    const hasGoogleAnalytics = computed(() => {
        const ga = googleAnalyticsConfig.value;
        if (!ga) {
            return false;
        }
        if (ga.enabled === false) {
            return false;
        }
        return typeof ga.measurementId === 'string' && ga.measurementId.trim().length > 0;
    });
    const googleAnalyticsId = computed(() => googleAnalyticsConfig.value?.measurementId || '');
    return {
        // Raw Config
        config,
        // Basic Info
        projectId,
        projectName,
        restaurantName,
        restaurantShortName,
        // Contact
        phone,
        email,
        website,
        // Address
        address,
        fullAddress,
        // Branding
        logo,
        colors,
        primaryColor,
        // SEO
        getMetaTags,
        getStructuredData,
        // Services
        isDeliveryEnabled,
        isPickupEnabled,
        deliveryInfo,
        pickupInfo,
        // Payment
        availablePaymentMethods,
        // Pricing
        formatPrice,
        // Social
        socialLinks,
        // Legal
        legalInfo,
        imprintUrl,
        privacyUrl,
        termsUrl,
        // Features
        features,
        isFeatureEnabled,
        // API
        apiConfig,
        apiBaseUrl,
        publicApiUrl,
        // Analytics
        analytics,
        googleAnalyticsConfig,
        hasGoogleAnalytics,
        googleAnalyticsId,
        // UI
        uiSettings,
        productInteractionSettings,
    };
}
/**
 * Helper: Inject structured data into document head
 */
export function injectStructuredData(data) {
    if (typeof document === 'undefined')
        return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data, null, 2);
    document.head.appendChild(script);
}
/**
 * Helper: Update document meta tags
 */
export function updateMetaTags(tags) {
    if (typeof document === 'undefined')
        return;
    // Title
    document.title = tags.title;
    // Meta Description
    updateMetaTag('name', 'description', tags.description);
    updateMetaTag('name', 'keywords', tags.keywords);
    // Open Graph
    if (tags.ogTitle)
        updateMetaTag('property', 'og:title', tags.ogTitle);
    if (tags.ogDescription)
        updateMetaTag('property', 'og:description', tags.ogDescription);
    if (tags.ogImage)
        updateMetaTag('property', 'og:image', tags.ogImage);
    // Twitter Card
    if (tags.twitterCard)
        updateMetaTag('name', 'twitter:card', tags.twitterCard);
    // Canonical
    if (tags.canonical) {
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement('link');
            link.rel = 'canonical';
            document.head.appendChild(link);
        }
        link.href = tags.canonical;
    }
}
function updateMetaTag(attribute, key, content) {
    let tag = document.querySelector(`meta[${attribute}="${key}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, key);
        document.head.appendChild(tag);
    }
    tag.content = content;
}
